import { useState, useRef, useEffect } from 'react';
import { useAuthStore } from '@/stores/use-auth-store';
import { useLocationStore } from '@/stores/use-location-store';

/**
 * Location switcher for the POS top bar.
 *
 * The POS only auto-selects a location at login when the user has EXACTLY ONE
 * active location (App.tsx). Users with 0 or 2+ locations were previously stuck
 * on "All Locations" (currentLocationId = null) with no way to pick one, which
 * left every location-scoped page (Floor Plan, New Order tables, etc.) empty.
 *
 * This lets a user choose among the locations they are already assigned to.
 */
export function LocationSwitcher() {
  const profile = useAuthStore((s) => s.profile);
  const user = useAuthStore((s) => s.user);
  const currentLocationId = useLocationStore((s) => s.currentLocationId);
  const currentLocationName = useLocationStore((s) => s.currentLocationName);
  const setLocation = useLocationStore((s) => s.setLocation);

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const locations = (profile?.locations ?? []).filter((l) => l.is_active);
  // Use the SAME identifiers App.tsx uses for the scoped persistence key
  // (user.sub + tenant id) so the choice survives reload / validateAndHydrate.
  const tenantId = profile?.tenant?.id;
  const userId = user?.sub;

  // No active locations — the user cannot transact. Say so explicitly rather
  // than showing a broken/empty picker.
  if (locations.length === 0) {
    return (
      <span className="text-danger-400" title="No active location assigned">
        No location — contact admin
      </span>
    );
  }

  const label = currentLocationName ?? 'Select location';
  const needsChoice = !currentLocationId;

  function choose(loc: { id: string; name: string; location_code: string }) {
    setLocation(loc.id, loc.name, loc.location_code, tenantId, userId);
    setOpen(false);
  }

  // Single location and already selected — just show it, no dropdown needed.
  if (locations.length === 1 && currentLocationId) {
    return <span className="text-neutral-300 truncate max-w-[160px]">{label}</span>;
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 transition-colors ${
          needsChoice ? 'text-warning-300 hover:text-warning-200' : 'text-neutral-300 hover:text-brand-300'
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Switch location"
        title="Switch location"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <circle cx="12" cy="11" r="3" />
        </svg>
        <span className="truncate max-w-[160px]">{needsChoice ? 'Select location' : label}</span>
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-full mt-1 z-50 min-w-[200px] rounded-lg bg-neutral-800 dark:bg-neutral-900 border border-neutral-700 shadow-xl py-1 animate-fade-in"
        >
          {locations.map((loc) => {
            const selected = loc.id === currentLocationId;
            return (
              <li key={loc.id} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => choose(loc)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-xs hover:bg-white/5 transition-colors ${
                    selected ? 'text-brand-300' : 'text-neutral-200'
                  }`}
                >
                  <span className="truncate">{loc.name}</span>
                  <span className="text-[10px] text-neutral-500">{loc.location_code}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
